import type { SxProps, Theme } from '@mui/material/styles'

import {
  borderRadius,
  componentTokens,
  shadows,
  spacing,
  typography
} from '../../KestrixDesignSystem.jsx'
import { colorTokens } from '../../tokens/colors.ts'
import type { KestrixSegmentedTabsVariant } from './KestrixSegmentedTabs.types.ts'

const RAISED_TABLIST_HEIGHT = '36px'
const RAISED_TRACK_PADDING = '2px'
const DEFAULT_TRACK_PADDING = '1px'
const DEFAULT_TRACK_RADIUS = '5px'
const TRACK_GAP = spacing[1]
const SELECTED_BORDER_WIDTH = '0.5px'
const RAISED_BORDER_WIDTH = '1px'
const FOCUS_RING_WIDTH = '2px'
const FOCUS_RING_OFFSET = 1
const DISABLED_OPACITY = 0.2

const defaultIndicatorSx = {
  position: 'absolute',
  zIndex: 0,
  top: DEFAULT_TRACK_PADDING,
  left: 0,
  height: `calc(100% - ${DEFAULT_TRACK_PADDING} * 2)`,
  boxSizing: 'border-box',
  borderRadius: borderRadius.sm,
  backgroundColor: colorTokens.segmentedTabs.selected.surface,
  border: `${SELECTED_BORDER_WIDTH} solid ${colorTokens.segmentedTabs.selected.border}`,
  pointerEvents: 'none'
} as const

const raisedIndicatorSx = {
  position: 'absolute',
  zIndex: 0,
  top: RAISED_TRACK_PADDING,
  left: 0,
  height: `calc(100% - ${RAISED_TRACK_PADDING} * 2)`,
  boxSizing: 'border-box',
  borderRadius: borderRadius.sm,
  backgroundColor: colorTokens.semantic.brand.base.white,
  border: `${RAISED_BORDER_WIDTH} solid ${colorTokens.global.neutral[500]}`,
  boxShadow: shadows.raisedSegment,
  pointerEvents: 'none'
} as const

/** Body S Medium on every segment of the raised control. Labels sit above the sliding segment. */
const raisedTabSx = {
  fontSize: typography.body.small.fontSize,
  lineHeight: typography.body.small.lineHeight,
  letterSpacing: typography.body.small.letterSpacing,
  fontWeight: typography.fontWeight.medium
} as const

/** The sliding segment carries the white fill. The selected label only changes color. */
const raisedSelectedLabelSx = {
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  color: colorTokens.text.bodyNeutral
} as const

export const segmentedTabButtonSx = (
  isSelected: boolean,
  variant: KestrixSegmentedTabsVariant
): SxProps<Theme> => ({
  flex: '1 1 0',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacing[1],
  px: spacing[2],
  py: spacing[1],
  position: 'relative',
  zIndex: 1,
  border: 'none',
  borderRadius: borderRadius.sm,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: typography.fontSize.xs,
  fontWeight: typography.fontWeight.medium,
  lineHeight: typography.lineHeight.normal,
  letterSpacing: typography.letterSpacing.tight,
  color: colorTokens.text.subtextNeutral,
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  ...(variant === 'raised' && raisedTabSx),
  ...(isSelected && variant === 'raised' && raisedSelectedLabelSx),
  '&:disabled': {
    opacity: DISABLED_OPACITY,
    cursor: 'not-allowed'
  },
  '&.Mui-focusVisible, &:focus-visible': {
    outline: `${FOCUS_RING_WIDTH} solid ${colorTokens.semantic.brand.primary}`,
    outlineOffset: FOCUS_RING_OFFSET
  }
})

export const indicatorSx = (
  variant: KestrixSegmentedTabsVariant
): SxProps<Theme> =>
  variant === 'raised' ? raisedIndicatorSx : defaultIndicatorSx

export const trackSx = (
  variant: KestrixSegmentedTabsVariant
): SxProps<Theme> => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'stretch',
  gap: TRACK_GAP,
  width: 'fit-content',
  maxWidth: '100%',
  p: variant === 'raised' ? RAISED_TRACK_PADDING : DEFAULT_TRACK_PADDING,
  height:
    variant === 'raised'
      ? RAISED_TABLIST_HEIGHT
      : componentTokens.input.height.sm,
  boxSizing: 'border-box',
  borderRadius: variant === 'raised' ? borderRadius.base : DEFAULT_TRACK_RADIUS,
  backgroundColor:
    variant === 'raised'
      ? colorTokens.global.neutral[500]
      : colorTokens.segmentedTabs.track,
  overflow: variant === 'raised' ? 'visible' : 'hidden'
})
